import React, { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring } from 'framer-motion';

interface CounterProps {
    value: number;
    direction?: 'up' | 'down';
}

const Counter: React.FC<CounterProps> = ({ value, direction = 'up' }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const initialValue = direction === 'down' ? value : 0;
    const motionValue = useMotionValue(initialValue);
    const springValue = useSpring(motionValue, {
        damping: 40,
        stiffness: 80, // Slightly slower, more premium feel
    });

    // Use a small amount of threshold to ensure it doesn't trigger 
    // until it's slightly more visible on scroll, but 0 margin for Hero
    const isInView = useInView(ref, { once: true, margin: '0px' });

    useEffect(() => {
        if (isInView) {
            motionValue.set(direction === 'down' ? 0 : value);
        }
    }, [motionValue, isInView, value, direction]);

    useEffect(() => {
        // Set initial text content immediately to prevent empty flashes
        if (ref.current) {
            ref.current.textContent = Intl.NumberFormat('en-US').format(initialValue);
        }

        const unsubscribe = springValue.on('change', (latest) => {
            if (ref.current) {
                ref.current.textContent = Intl.NumberFormat('en-US').format(
                    Math.floor(latest)
                );
            }
        });

        return () => unsubscribe();
    }, [springValue, initialValue]);

    return <span ref={ref} />;
};

export default Counter;
